import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Printer, Share2, Check, X, Copy, MessageSquare, Edit3, Shield, Info, FileCheck } from "lucide-react";
import CollateralPreview from "../../components/collateral/CollateralPreview";
import CollateralChatbot from "../../components/collateral/CollateralChatbot";
import VisualEditor from "../../components/visualEditor/VisualEditor";
import InspectorPanel from "../../components/visualEditor/InspectorPanel";
import SaveChangesDialog from "../../components/visualEditor/SaveChangesDialog";
import { loadFontsFromData } from "../../utils/fontLoader";
import { useImageProxy } from "../../hooks/useImageProxy";
import { useVisualEditor } from "../../hooks/useVisualEditor";
import { useAuth } from "../../context/AuthContext";
import { useSubscriptionLimits } from "../../context/SubscriptionLimitsContext";
import { useReactToPrint } from "react-to-print";
import CompactUsageIndicator from "../../components/tailspin/CompactUsageIndicator";

export default function TailspinCollateral() {
  const { slug } = useParams();
  const location = useLocation();
  // When navigated to with ?source=aura the data lives in new_entity_insight_collateral
  const isAuraSource = useMemo(
    () => new URLSearchParams(location.search).get('source') === 'aura',
    [location.search]
  );
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  // Version management
  const [allVersions, setAllVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  // Chatbot states
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Shareable link state
  const [linkCopied, setLinkCopied] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  
  // Prospect-only shareable link state
  const [prospectLinkCopied, setProspectLinkCopied] = useState(false);
  const [showProspectLinkModal, setShowProspectLinkModal] = useState(false);
  const [prospectShareableLink, setProspectShareableLink] = useState('');
  
  // Compliance modal state
  const [showComplianceModal, setShowComplianceModal] = useState(false);

  // Sidebar state
  const [sidebarMode, setSidebarMode] = useState('chat'); // 'chat' | 'edit'
  const [toast, setToast] = useState(null);

  // Get subscription limits from context
  const { usage: subscriptionUsage, loading: loadingUsageStats } = useSubscriptionLimits();

  // Transform context data to match CompactUsageIndicator format
  const usageStats = subscriptionUsage ? {
    collaterals: {
      allowed: subscriptionUsage.plan.limits.collaterals,
      created: subscriptionUsage.usage.collaterals,
      remaining: subscriptionUsage.usage.collateralsRemaining,
      percentage: Math.round((subscriptionUsage.usage.collaterals / subscriptionUsage.plan.limits.collaterals) * 100)
    },
    editsPerCollateral: {
      allowed: subscriptionUsage.plan.limits.editsPerCollateral
    }
  } : null;

  // Disabled image conversion - use original URLs
  const convertedBlobUrls = null;
  const imagesLoading = false;

  // Converted data with blob URLs
  const [convertedData, setConvertedData] = useState(null);

  const [dataVersion, setDataVersion] = useState(0);

  // Save functionality states
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const originalDataRef = useRef(null);

  // Auto-fix (preview error -> send to backend) state
  const [isAutoFixing, setIsAutoFixing] = useState(false);
  const [autoFixingVersionKey, setAutoFixingVersionKey] = useState(null);
  const sentPreviewErrorKeysRef = useRef(new Set());
  const currentVersionIndexRef = useRef(0);
  const currentSelectedVersionIdRef = useRef(null);
  const pendingAutoFixRef = useRef(null);

  /**
   * Handle visual editor data changes
   */
  const handleVisualDataChange = useCallback(async (newData) => {
    console.log("🎨 Visual edit - data changed:", newData);

    // Store original data on first edit
    if (originalDataRef.current === null) {
      originalDataRef.current = JSON.parse(JSON.stringify(data));
    }

    // Update local data immediately for real-time preview
    setData(newData);

    // Mark as unsaved
    setHasUnsavedChanges(true);

    // Increment version to force re-render
    setDataVersion(prev => prev + 1);
  }, [data]);

  // Visual editor hook
  const {
    selectedElement,
    isVisualEditMode,
    hoveredElement,
    handleElementClick,
    handleElementHover,
    handleElementLeave,
    updateProperty,
    toggleVisualEditMode,
    clearSelection,
    setIsVisualEditMode,
    setSelectedElement
  } = useVisualEditor(convertedData || data, handleVisualDataChange);

  // Wrap updateProperty to show toast notifications
  const updatePropertyWithFeedback = useCallback((path, value) => {
    console.log('🔧 Update requested:', path, '→', value);

    try {
      updateProperty(path, value);
      setToast({ type: 'success', message: 'Updated!' });
      setTimeout(() => setToast(null), 2000);
    } catch (error) {
      console.error('❌ Update failed:', error);
      setToast({ type: 'error', message: 'Update failed' });
      setTimeout(() => setToast(null), 3000);
    }
  }, [updateProperty]);

  const handleTabChange = (mode) => {
    if (mode === 'chat' && sidebarMode === 'edit' && hasUnsavedChanges) {
      setShowSaveDialog(true);
      return;
    }

    setSidebarMode(mode);
    if (mode === 'edit') {
      setIsVisualEditMode(true);
    } else {
      setIsVisualEditMode(false);
      clearSelection();
    }
  };

  const extractVersionContent = (version) => {
    // Check for triple-nested structure: version.collateral.collateral.collateral
    const tripleNested = version?.collateral?.collateral?.collateral;
    if (tripleNested && typeof tripleNested === "object") {
      return {
        data: tripleNested.data ?? null,
        template: tripleNested.template ?? null,
        compliance: tripleNested.compilance ?? tripleNested.compliance ?? null,
      };
    }

    // Check for double-nested structure: version.collateral.collateral
    const doubleNested = version?.collateral?.collateral;
    if (doubleNested && typeof doubleNested === "object") {
      return {
        data: doubleNested.data ?? null,
        template: doubleNested.template ?? null,
        compliance: doubleNested.compilance ?? doubleNested.compliance ?? null,
      };
    }

    // Check for single-nested structure: version.collateral
    const singleNested = version?.collateral;
    if (singleNested && typeof singleNested === "object") {
      return {
        data: singleNested.data ?? null,
        template: singleNested.template ?? null,
        compliance: singleNested.compilance ?? singleNested.compliance ?? null,
      };
    }

    // Fallback to direct properties
    return {
      data: version?.data ?? null,
      template: version?.template ?? null,
      compliance: version?.compilance ?? version?.compliance ?? null,
    };
  };

  const getVersionKey = useCallback((version, index) => {
    const id = version?.id ?? null;
    return id ? `id:${id}` : `idx:${index}`;
  }, []);

  // Fetch the latest versions from the correct data source
  const refreshVersions = useCallback(async () => {
    const response = isAuraSource
      ? await apiService.getInsightCollateralBySlug(slug)
      : await apiService.gettailspinCollateralBySlug(slug);
    return Array.isArray(response?.data?.data)
      ? response.data.data
      : [response?.data?.data].filter(Boolean);
  }, [slug, isAuraSource]);

  const applyVersionFromList = useCallback(async (versions, index) => {
    const v = versions?.[index];
    const { data: versionData, template: versionTemplate, compliance: versionCompliance } = extractVersionContent(v);
    setData(versionData);
    setTemplate(versionTemplate);
    setCompliance(versionCompliance);
    await loadFontsFromData(versionData);
  }, []);

  // Usage stats now come from SubscriptionLimitsContext automatically

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    
    let pollInterval = null;

    async function fetchData() {
      try {
        const response = isAuraSource
          ? await apiService.getInsightCollateralBySlug(slug)
          : await apiService.gettailspinCollateralBySlug(slug);

        console.log("Response:", response);

        // Handle array response (multiple versions)
        const versions = Array.isArray(response?.data?.data)
          ? response.data.data
          : [response?.data?.data].filter(Boolean);

        if (versions.length === 0) {
          setError("No collateral data found.");
          return;
        }

        // Check if any version is in "running" status
        const hasRunningVersion = versions.some(v => v.status === 'running');
        console.log("Has running version:", hasRunningVersion);

        setAllVersions(versions);
        
        // Find first "done" version to show, or fall back to index 0
        const firstDoneIndex = versions.findIndex(v => v.status === 'done' || !v.status);
        setCurrentVersionIndex(firstDoneIndex >= 0 ? firstDoneIndex : 0);

        // Build chat history from versions (only include "done" versions)
        const historyMessages = [];
        // Clone and reverse to process chronologically (oldest to newest)
        const chronologicalVersions = [...versions].reverse();

        chronologicalVersions.forEach((version, index) => {
          const description = version.description;
          const status = version.status;

          if (description) {
            historyMessages.push({
              id: `hist-user-${version.id || index}`,
              type: 'user',
              content: description,
              timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });

            if (status === 'running') {
              historyMessages.push({
                id: `hist-bot-${version.id || index}`,
                type: 'bot',
                content: `⏳ Generating version ${index + 1}...`,
                timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              });
            } else if (status === 'failed') {
              historyMessages.push({
                id: `hist-bot-${version.id || index}`,
                type: 'bot',
                content: `❌ Version ${index + 1} generation failed.`,
                timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                isError: true
              });
            } else {
              historyMessages.push({
                id: `hist-bot-${version.id || index}`,
                type: 'bot',
                content: `✓ Version ${index + 1} generated successfully.`,
                timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              });
            }
          }
        });

        setMessages(historyMessages);

        // Set initial data from the first "done" version
        const initialVersion = versions[firstDoneIndex >= 0 ? firstDoneIndex : 0];
        console.log("initialVersion", initialVersion);
        const { data: initialData, template: initialTemplate, compliance: initialCompliance } = extractVersionContent(initialVersion);
        
        // Only set data/template if not running
        if (initialVersion.status !== 'running') {
          setData(initialData);
          setTemplate(initialTemplate);
          setCompliance(initialCompliance);
          
          // Load fonts from data
          await loadFontsFromData(initialData);
        } else {
          // If the only version is running, set minimal data to avoid errors
          setData(null);
          setTemplate(null);
          setCompliance(null);
        }

        currentSelectedVersionIdRef.current = initialVersion?.id ?? null;
        currentVersionIndexRef.current = firstDoneIndex >= 0 ? firstDoneIndex : 0;

        // Usage statistics are fetched automatically by SubscriptionLimitsContext

        console.log("Full versions:", versions);
        console.log("Showing version:", firstDoneIndex >= 0 ? firstDoneIndex : 0);
        
        // If there's a running version, set up a polling interval (tailspin source only)
        if (hasRunningVersion && !isAuraSource) {
          console.log("🔄 Setting up polling for running version");
          pollInterval = setInterval(async () => {
            try {
              const pollResponse = await apiService.gettailspinCollateralBySlug(slug);
              const pollVersions = Array.isArray(pollResponse?.data?.data)
                ? pollResponse.data.data
                : [pollResponse?.data?.data].filter(Boolean);
              
              const stillRunning = pollVersions.some(v => v.status === 'running');
              
              if (!stillRunning) {
                console.log("✅ No more running versions, clearing interval");
                if (pollInterval) clearInterval(pollInterval);
                // Reload the page data
                fetchData();
              } else {
                console.log("⏳ Still running, continuing to poll...");
              }
            } catch (err) {
              console.error("Error polling for updates:", err);
            }
          }, 3000); // Poll every 3 seconds
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch template or data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    
    // Cleanup function to clear polling interval on unmount
    return () => {
      if (pollInterval) {
        console.log("🧹 Cleaning up polling interval");
        clearInterval(pollInterval);
      }
    };
  }, [slug, isAuraSource]);

  // Keep refs in sync so async auto-fix doesn't use stale selection
  useEffect(() => {
    currentVersionIndexRef.current = currentVersionIndex;
    currentSelectedVersionIdRef.current = allVersions?.[currentVersionIndex]?.id ?? null;
  }, [currentVersionIndex, allVersions]);

  const handleVersionSwitch = async (index) => {
    if (index >= 0 && index < allVersions.length) {
      const version = allVersions[index];
      
      // Don't switch to running or failed versions
      if (version.status === 'running' || version.status === 'failed') {
        console.log(`Cannot switch to version ${index} with status: ${version.status}`);
        return;
      }
      
      setCurrentVersionIndex(index);
      const { data: versionData, template: versionTemplate, compliance: versionCompliance } = extractVersionContent(version);
      setData(versionData);
      setTemplate(versionTemplate);
      setCompliance(versionCompliance);

      // Load fonts for the switched version
      await loadFontsFromData(versionData);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const messageContent = inputValue.trim();

    setMessages(prev => [...prev, {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    setInputValue("");
    setIsSending(true);

    try {
      if (isAuraSource) {
        const editResponse = await apiService.editCollateral(slug, messageContent);
        if (!editResponse?.data?.success) throw new Error("Failed to update collateral");
      } else {
        const response = await apiService.editTailspinCollateral(slug, messageContent, currentVersionIndex);
        if (!response?.data?.success) throw new Error(response?.data?.error || "Failed to update collateral");
      }

      const versions = await refreshVersions();
      setAllVersions(versions);
      setCurrentVersionIndex(0);

      const newVersion = versions[0];
      const { data: newData, template: newTemplate } = extractVersionContent(newVersion);
      setData(newData);
      setTemplate(newTemplate);
      await loadFontsFromData(newData);

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: "bot",
        content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        data: newData,
        versions: versions
      }]);
    } catch (err) {
      console.error("Error editing collateral:", err);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: "bot",
        content: (err.response?.data?.error) ? err.response?.data?.error : "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleErrorReport = async (errorMessage, meta = {}) => {
    if (!slug || !errorMessage) return;

    const versionIndexAtError = typeof meta.versionIndex === 'number' ? meta.versionIndex : currentVersionIndexRef.current;
    const versionAtError = allVersions?.[versionIndexAtError];
    const versionKey = meta.versionKey || getVersionKey(versionAtError, versionIndexAtError);
    const errorKey = `${versionKey}::${errorMessage}`;

    // Dedupe: never re-send the same error for the same version during this session.
    if (sentPreviewErrorKeysRef.current.has(errorKey)) return;
    sentPreviewErrorKeysRef.current.add(errorKey);

    // If we're already sending something, don't pile on (prevents races).
    if (isSending) {
      pendingAutoFixRef.current = { errorMessage, meta };
      return;
    }

    setIsAutoFixing(true);
    setAutoFixingVersionKey(versionKey);
    setIsSending(true);

    // Let the user know we're handling it
    const botMessage = {
      id: Date.now(),
      type: "bot",
      content: "⚠️ I hit a preview rendering error. Sending it to the editor to auto-fix…",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages(prev => [...prev, botMessage]);

    try {
      if (isAuraSource) {
        const editResponse = await apiService.editCollateral(slug, errorMessage);
        if (!editResponse?.data?.success) {
          throw new Error("Failed to auto-fix collateral");
        }
      } else {
        const errorCollateralId = versionAtError?.id ?? null;
        const response = await apiService.editTailspinCollateral(
          slug,
          errorMessage,
          versionIndexAtError,
          errorCollateralId,
          true
        );
        if (!response?.data?.success) {
          throw new Error(response?.data?.error || "Failed to auto-fix collateral");
        }
      }

      const versions = await refreshVersions();

      setAllVersions(versions);

      // If user stayed on the version that errored, switch them to the latest fix (index 0).
      // If they switched away, keep their current selection by id (best-effort).
      if (currentVersionIndexRef.current === versionIndexAtError) {
        setCurrentVersionIndex(0);
        await applyVersionFromList(versions, 0);
      } else {
        const selectedId = currentSelectedVersionIdRef.current;
        const idxById = selectedId ? versions.findIndex(v => v?.id === selectedId) : -1;
        const nextIndex = idxById >= 0 ? idxById : Math.min(currentVersionIndexRef.current, versions.length - 1);
        setCurrentVersionIndex(nextIndex);
        await applyVersionFromList(versions, nextIndex);
      }

      const doneMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "✓ I generated a fix. The collateral versions have been refreshed.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        versions: versions,
      };
      setMessages(prev => [...prev, doneMessage]);
    } catch (err) {
      console.error("Error auto-fixing collateral:", err);
      const errorMsg = {
        id: Date.now() + 1,
        type: "bot",
        content: (err.response?.data?.error) ? err.response?.data?.error : "❌ Auto-fix failed. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsAutoFixing(false);
      setAutoFixingVersionKey(null);
      setIsSending(false);
    }
  };

  // If a preview error happened while we were already sending, retry once when idle.
  useEffect(() => {
    if (isSending) return;
    if (!pendingAutoFixRef.current) return;
    const pending = pendingAutoFixRef.current;
    pendingAutoFixRef.current = null;
    handleErrorReport(pending.errorMessage, pending.meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending]);

  const handleGetShareableLink = () => {
    if (!slug) return;
    const domain = user?.email?.split('@')[1].split('.')[0];
    // Use environment variable if available, otherwise fallback to current origin
   
    
    const baseUrl = process.env.REACT_APP_SHAREABLE_URL || "https://microsite.heyparrot.ai";
    const link = `${baseUrl}/${domain}/${slug}`;
    
    console.log("shareabelink", link);
    setShareableLink(link);
    setShowLinkModal(true);
  };

  const handleCopyLink = async () => {
    if (!shareableLink) return;

    try {
      await navigator.clipboard.writeText(shareableLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy link:', fallbackErr);
        alert(`Shareable link: ${shareableLink}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleGetProspectShareableLink = () => {
    if (!slug) return;
    const baseUrl = process.env.REACT_APP_SHAREABLE_URL || "https://microsite.heyparrot.ai";
    const link = `${baseUrl}/prospect/${slug}`;
    
    console.log("prospect shareable link", link);
    setProspectShareableLink(link);
    setShowProspectLinkModal(true);
  };

  const handleCopyProspectLink = async () => {
    if (!prospectShareableLink) return;

    try {
      await navigator.clipboard.writeText(prospectShareableLink);
      setProspectLinkCopied(true);
      setTimeout(() => setProspectLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = prospectShareableLink;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setProspectLinkCopied(true);
        setTimeout(() => setProspectLinkCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy prospect link:', fallbackErr);
        alert(`Prospect shareable link: ${prospectShareableLink}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleQuickEdit = async (message) => {
    if (sidebarMode !== 'chat') {
      handleTabChange('chat');
    }

    if (!message.trim() || isSending) return;

    const messageContent = message.trim();

    setMessages(prev => [...prev, {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
    setIsSending(true);

    try {
      if (isAuraSource) {
        const editResponse = await apiService.editCollateral(slug, messageContent);
        if (!editResponse?.data?.success) throw new Error("Failed to update collateral");
      } else {
        const response = await apiService.editTailspinCollateral(slug, messageContent, currentVersionIndex);
        if (!response?.data?.success) throw new Error("Failed to update collateral");
      }

      const versions = await refreshVersions();
      setAllVersions(versions);
      setCurrentVersionIndex(0);

      const newVersion = versions[0];
      const { data: newData, template: newTemplate, compliance: newCompliance } = extractVersionContent(newVersion);
      setData(newData);
      setTemplate(newTemplate);
      setCompliance(newCompliance);
      await loadFontsFromData(newData);

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: "bot",
        content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        data: newData,
        versions: versions
      }]);
    } catch (err) {
      console.error("Error editing collateral:", err);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: "bot",
        content: "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      }]);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Save visual editor changes to the correct backend table
   */
  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      let response;

      if (isAuraSource) {
        response = await apiService.updateInsightCollateral(slug, { data, template });
      } else {
        const currentCollateral = allVersions[currentVersionIndex];
        if (!currentCollateral?.id) {
          throw new Error('No collateral ID found');
        }
        response = await apiService.updateTailspinCollateral(
          currentCollateral.id,
          {
            collateral: {
              collateral: {
                title: currentCollateral.collateral?.collateral?.title,
                data: data,
                template: template
              }
            }
          }
        );
      }

      if (response?.data?.success) {
        originalDataRef.current = JSON.parse(JSON.stringify(data));
        setHasUnsavedChanges(false);
        setShowSaveDialog(false);
        setToast({ type: 'success', message: 'Saved successfully!' });
        setTimeout(() => setToast(null), 2000);
      } else {
        throw new Error(response?.data?.message || 'Failed to save');
      }
    } catch (error) {
      console.error('❌ Error saving collateral:', error);
      setToast({ type: 'error', message: `Failed to save: ${error.message}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Discard changes and revert to original data
   */
  const handleDiscardChanges = () => {
    if (originalDataRef.current) {
      setData(originalDataRef.current);
      setDataVersion(prev => prev + 1);
    }
    setHasUnsavedChanges(false);
    setShowSaveDialog(false);
    // Switch to chat mode
    handleTabChange('chat');
    originalDataRef.current = null;
  };

  /**
   * Cancel closing (stay in edit mode)
   */
  const handleCancelClose = () => {
    setShowSaveDialog(false);
  };

  // Image conversion disabled - just use original data
  useEffect(() => {
    if (!data) {
      setConvertedData(null);
      return;
    }

    // Use original data without conversion
    setConvertedData(data);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>Loading collateral...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center p-6 rounded-lg" style={{ color: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  // Check if current version is running
  const currentVersion = allVersions[currentVersionIndex];
  const isCurrentVersionRunning = currentVersion?.status === 'running';

  if (isCurrentVersionRunning) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }} className="text-lg font-semibold mb-2">Generating collateral...</p>
          <p style={{ color: '#666' }} className="text-sm">This may take a minute. You can refresh the page to check progress.</p>
        </div>
      </div>
    );
  }

  if (!template || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div style={{ color: '#0A0A10' }}>No template or data received.</div>
      </div>
    );
  }


  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Left Sidebar - Chatbot or Inspector - FIXED WIDTH */}
      <div className="w-96 flex flex-col flex-shrink-0 border-r border-gray-200 bg-white">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleTabChange('chat')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'chat' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </button>
          <button
            onClick={() => handleTabChange('edit')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'edit' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            <Edit3 className="w-4 h-4" />
            Editor
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          <div className={`absolute inset-0 flex flex-col transition-opacity duration-200 ${sidebarMode === 'chat' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
            <CollateralChatbot
              messages={messages}
              inputValue={inputValue}
              isSending={isSending}
              onInputChange={handleInputChange}
              onSendMessage={handleSendMessage}
              onKeyPress={handleKeyPress}
              allVersions={allVersions}
              currentVersionIndex={currentVersionIndex}
              onVersionSwitch={handleVersionSwitch}
              usageStats={usageStats}
              currentVersions={allVersions.length}
              hasRunningVersion={allVersions.some(v => v.status === 'running')}
            />
          </div>

          {sidebarMode === 'edit' && (
            <div className="absolute inset-0 z-10 h-full overflow-auto bg-white">
              <InspectorPanel
                selectedElement={selectedElement}
                onUpdateProperty={updatePropertyWithFeedback}
                onClose={clearSelection}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Collateral Preview with Visual Editor - TAKES REMAINING SPACE */}
      <div className="flex-1 overflow-y-auto border-l" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
        <VisualEditor
          isVisualEditMode={isVisualEditMode}
          selectedElement={selectedElement}
          hoveredElement={hoveredElement}
          handleElementClick={handleElementClick}
          handleElementHover={handleElementHover}
          handleElementLeave={handleElementLeave}
          clearSelection={clearSelection}
          toast={toast}
        >
          {/* Header Toolbar */}
          <div className="sticky top-0 z-10 border-b px-6 py-4 flex items-center justify-between no-print" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
            <h2 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>Preview</h2>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm bg-green-600 hover:bg-green-700"
                >
                  Save Changes
                </button>
              )}
              <button
                onClick={handleGetShareableLink}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                style={{ backgroundColor: '#FF6B5A' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e55a4a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF6B5A';
                }}
                title="Get shareable link"
              >
                <Share2 className="w-4 h-4" />
                <span>Get Shareable Link</span>
              </button>
              <button
                onClick={handleGetProspectShareableLink}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                style={{ backgroundColor: '#4A90E2' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#357ABD';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#4A90E2';
                }}
                title="Get shareable link for prospect only (requires domain verification)"
              >
                <Share2 className="w-4 h-4" />
                <span>Share to Prospect Only</span>
              </button>
              {compliance && (
                <button
                  onClick={() => setShowComplianceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                  style={{ backgroundColor: '#FF6B5A' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e55a4a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FF6B5A';
                  }}
                  title="View compliance information"
                >
                  <FileCheck className="w-4 h-4" />
                  <span>Get Compliance</span>
                </button>
              )}
              {/* <button
                onClick={reactToPrintFn}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                style={{ backgroundColor: '#FF6B5A' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                title="Print collateral"
              >
                <Printer className="w-4 h-4" />
                <span>Download as PDF</span>
              </button> */}

              
            </div>
          </div>

          {/* Preview Content */}
          <div ref={contentRef} className="p-6">
            <CollateralPreview
              key={`preview-${currentVersionIndex}-${dataVersion}`}
              template={template}
              data={convertedData || data}
              withContainer={false}
              onErrorReport={handleErrorReport}
              versionIndex={currentVersionIndex}
              versionKey={getVersionKey(allVersions?.[currentVersionIndex], currentVersionIndex)}
              isResolvingError={isAutoFixing && autoFixingVersionKey === getVersionKey(allVersions?.[currentVersionIndex], currentVersionIndex)}
              onQuickEdit={handleQuickEdit}
            />
          </div>
        </VisualEditor>
      </div>

      {/* Shareable Link Modal */}
      {showLinkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowLinkModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#F8F7F4' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                Shareable Link
              </h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                style={{ color: '#0A0A10' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                Copy this link to share:
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: 'white' }}>
                <input
                  type="text"
                  value={shareableLink}
                  readOnly
                  className="flex-1 outline-none text-sm"
                  style={{ color: '#0A0A10' }}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyLink}
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: linkCopied ? '#10b981' : '#2464f7',
                    color: 'white'
                  }}
                  title="Copy link"
                >
                  {linkCopied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              {linkCopied && (
                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                  ✓ Link copied to clipboard!
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: '#2464f7',
                  color: 'white'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2464f7'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prospect-Only Shareable Link Modal */}
      {showProspectLinkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowProspectLinkModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#F8F7F4' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                Prospect-Only Shareable Link
              </h3>
              <button
                onClick={() => setShowProspectLinkModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                style={{ color: '#0A0A10' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    This link requires Google sign-in. Only users with the prospect's email domain can access this collateral.
                  </p>
                </div>
              </div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                Copy this link to share:
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: 'white' }}>
                <input
                  type="text"
                  value={prospectShareableLink}
                  readOnly
                  className="flex-1 outline-none text-sm"
                  style={{ color: '#0A0A10' }}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyProspectLink}
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: prospectLinkCopied ? '#10b981' : '#4A90E2',
                    color: 'white'
                  }}
                  title="Copy link"
                >
                  {prospectLinkCopied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              {prospectLinkCopied && (
                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                  ✓ Link copied to clipboard!
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowProspectLinkModal(false)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: '#4A90E2',
                  color: 'white'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#357ABD'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#4A90E2'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Modal */}
      {showComplianceModal && compliance && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowComplianceModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#F8F7F4' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                  Content Compliance
                </h3>
              </div>
              <button
                onClick={() => setShowComplianceModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                style={{ color: '#0A0A10' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-blue-600">{compliance.score || 'N/A'}</span>
                    <span className="text-lg text-gray-600">/ 100</span>
                  </div>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                      style={{ width: `${compliance.score || 0}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 italic">AI-Generated Verification Score</p>
              </div>
              
              {compliance.note && (
                <div className="pt-4 border-t border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Compliance Assessment:</p>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{compliance.note}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowComplianceModal(false)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: '#2464f7',
                  color: 'white'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2464f7'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Changes Dialog */}
      <SaveChangesDialog
        isOpen={showSaveDialog}
        onSave={handleSaveChanges}
        onDiscard={handleDiscardChanges}
        onCancel={handleCancelClose}
        isSaving={isSaving}
      />
    </div>
  );
}
